import React, { useEffect, useMemo, useState } from 'react';
import { useAppContext } from '../../App';
import { api } from '../../apiService';
import { toast } from 'react-toastify';

const toMonthId = (year, month) => `${year}-${String(month).padStart(2, '0')}`;

export default function MonthlyClosingSection() {
  const { user, currentStationId, allStations } = useAppContext();
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [notes, setNotes] = useState('');
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const stationName = useMemo(() => allStations.find(s => s.id === currentStationId)?.name || 'Selected Station', [allStations, currentStationId]);
  const monthId = useMemo(() => toMonthId(year, month), [year, month]);

  const currentRow = useMemo(() => {
    return rows.find(r => r.stationId === currentStationId && r.monthId === monthId) || null;
  }, [rows, currentStationId, monthId]);

  const isClosed = !!currentRow?.isClosed;

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      if (!currentStationId || currentStationId === 'all') {
        if (mounted) { setRows([]); setIsLoading(false); }
        return;
      }
      setIsLoading(true);
      try {
        const data = await api.get('month_closings', { where: { stationId: currentStationId }, orderBy: 'monthId DESC', limit: 24 });
        if (mounted) setRows(data || []);
      } catch (err) {
        toast.error(`Failed to load month closing status: ${err.message}`);
      } finally {
        if (mounted) setIsLoading(false);
      }
    };
    load();
    return () => { mounted = false; };
  }, [currentStationId]);

  const handleClose = async () => {
    if (!currentStationId || currentStationId === 'all') return;
    setIsSubmitting(true);
    try {
      const payload = {
        stationId: currentStationId,
        monthId,
        isClosed: 1,
        closedAt: new Date().toISOString().slice(0, 19).replace('T', ' '),
        closedBy: user?.email || user?.name || '',
        notes: notes || ''
      };
      if (currentRow?.id) await api.put('month_closings', currentRow.id, payload);
      else await api.post('month_closings', payload);
      toast.success(`Month closed: ${monthId}`);
      const data = await api.get('month_closings', { where: { stationId: currentStationId }, orderBy: 'monthId DESC', limit: 24 });
      setRows(data || []);
    } catch (err) {
      toast.error(`Failed to close month: ${err.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReopen = async () => {
    if (!currentRow?.id) return;
    setIsSubmitting(true);
    try {
      const payload = {
        stationId: currentStationId,
        monthId,
        isClosed: 0,
        closedAt: null,
        closedBy: null,
        notes: notes || currentRow?.notes || ''
      };
      await api.put('month_closings', currentRow.id, payload);
      toast.success(`Month re-opened: ${monthId}`);
      const data = await api.get('month_closings', { where: { stationId: currentStationId }, orderBy: 'monthId DESC', limit: 24 });
      setRows(data || []);
    } catch (err) {
      toast.error(`Failed to re-open month: ${err.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!user || user.role !== 'admin') {
    return (
      <div className="max-w-2xl mx-auto bg-white p-8 rounded-2xl shadow-lg">
        <h1 className="text-2xl font-bold text-gray-800">Monthly Closing</h1>
        <p className="text-gray-500 mt-2">Admin access required.</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto space-y-8">
      <div>
        <h1 className="text-4xl font-bold text-gray-800">Monthly Closing</h1>
        <p className="text-gray-500 mt-1">Lock a month to prevent changes in invoices, payments, expenses, bills, and salaries.</p>
      </div>

      <div className="bg-white p-8 rounded-2xl shadow-lg">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
          <div className="md:col-span-2">
            <div className="text-sm font-semibold text-gray-500">Station</div>
            <div className="text-xl font-bold text-gray-900">{stationName}</div>
            <div className="text-xs text-gray-400">{currentStationId}</div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Year</label>
            <select value={year} onChange={e => setYear(Number(e.target.value))} className="mt-1 block w-full p-3 bg-gray-50 rounded-md">
              {Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - i).map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Month</label>
            <select value={month} onChange={e => setMonth(Number(e.target.value))} className="mt-1 block w-full p-3 bg-gray-50 rounded-md">
              {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                <option key={m} value={m}>{String(m).padStart(2, '0')}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="mt-6">
          <label className="block text-sm font-medium text-gray-700">Notes (optional)</label>
          <input value={notes} onChange={e => setNotes(e.target.value)} className="mt-1 block w-full p-3 bg-gray-50 rounded-md" placeholder="Reason / notes for closing..." />
        </div>

        <div className="mt-6 flex items-center justify-between">
          <div className={`px-4 py-2 rounded-lg text-sm font-bold ${isClosed ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
            Status: {isClosed ? 'CLOSED' : 'OPEN'} ({monthId})
          </div>
          <div className="flex gap-2">
            {!isClosed ? (
              <button type="button" onClick={handleClose} disabled={isSubmitting || !currentStationId || currentStationId === 'all'} className="bg-rose-600 text-white font-bold py-3 px-6 rounded-lg shadow-lg hover:bg-rose-700 disabled:opacity-50">
                Close Month
              </button>
            ) : (
              <button type="button" onClick={handleReopen} disabled={isSubmitting} className="bg-gray-700 text-white font-bold py-3 px-6 rounded-lg shadow-lg hover:bg-gray-800 disabled:opacity-50">
                Re-open Month
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="bg-white p-8 rounded-2xl shadow-lg">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">History</h2>
        {isLoading ? (
          <div className="text-gray-500">Loading...</div>
        ) : rows.length === 0 ? (
          <div className="text-gray-400 italic">No month closing records yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm text-left text-gray-600">
              <thead className="text-xs uppercase bg-gray-50 text-gray-700">
                <tr>
                  <th className="px-4 py-3">Month</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Closed By</th>
                  <th className="px-4 py-3">Closed At</th>
                  <th className="px-4 py-3">Notes</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {rows.map(r => (
                  <tr key={r.id}>
                    <td className="px-4 py-3 font-mono">{r.monthId}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded text-xs font-bold ${r.isClosed ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                        {r.isClosed ? 'CLOSED' : 'OPEN'}
                      </span>
                    </td>
                    <td className="px-4 py-3">{r.closedBy || '--'}</td>
                    <td className="px-4 py-3">{r.closedAt ? new Date(r.closedAt).toLocaleString() : '--'}</td>
                    <td className="px-4 py-3">{r.notes || '--'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

